import api from './api';
import {
  DEFAULT_STATUS_OPTIONS_MAP,
  DEFAULT_STATUS_OPTIONS_RAW_MAP,
  formatStatusOptionsMap,
  RawStatusOptionsMap,
  StatusOptionsMap,
} from '../config/status-options';

interface OrderStatusOptionsPayload {
  raw_map: RawStatusOptionsMap;
  formatted_map: StatusOptionsMap;
  default_raw_map: RawStatusOptionsMap;
}

interface OrderStatusOptionsResponse {
  success: boolean;
  data?: OrderStatusOptionsPayload;
  message?: string;
}

class OrderStatusOptionsService {
  private cachedPayload: OrderStatusOptionsPayload | null = null;

  private loadingPromise: Promise<OrderStatusOptionsPayload> | null = null;

  private getFallbackPayload(): OrderStatusOptionsPayload {
    return {
      raw_map: DEFAULT_STATUS_OPTIONS_RAW_MAP,
      formatted_map: DEFAULT_STATUS_OPTIONS_MAP,
      default_raw_map: DEFAULT_STATUS_OPTIONS_RAW_MAP,
    };
  }

  async get(forceRefresh: boolean = false): Promise<OrderStatusOptionsPayload> {
    if (!forceRefresh && this.cachedPayload) {
      return this.cachedPayload;
    }

    if (!forceRefresh && this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = api.get<OrderStatusOptionsResponse>('/order-status-options')
      .then((response) => {
        const payload = response.data?.data;

        if (!payload?.raw_map) {
          this.cachedPayload = this.getFallbackPayload();
          return this.cachedPayload;
        }

        this.cachedPayload = {
          raw_map: payload.raw_map,
          formatted_map: payload.formatted_map || formatStatusOptionsMap(payload.raw_map),
          default_raw_map: payload.default_raw_map || DEFAULT_STATUS_OPTIONS_RAW_MAP,
        };

        return this.cachedPayload;
      })
      .catch(() => {
        this.cachedPayload = this.getFallbackPayload();
        return this.cachedPayload;
      })
      .finally(() => {
        this.loadingPromise = null;
      });

    return this.loadingPromise;
  }

  async update(rawMap: RawStatusOptionsMap): Promise<OrderStatusOptionsPayload> {
    const response = await api.put<OrderStatusOptionsResponse>('/order-status-options', {
      raw_map: rawMap,
    });

    const payload = response.data?.data;

    if (!payload?.raw_map) {
      throw new Error(response.data?.message || '保存订单状态选项失败');
    }

    this.cachedPayload = {
      raw_map: payload.raw_map,
      formatted_map: payload.formatted_map || formatStatusOptionsMap(payload.raw_map),
      default_raw_map: payload.default_raw_map || DEFAULT_STATUS_OPTIONS_RAW_MAP,
    };

    return this.cachedPayload;
  }

  clearCache(): void {
    this.cachedPayload = null;
    this.loadingPromise = null;
  }
}

export const orderStatusOptionsService = new OrderStatusOptionsService();

export default orderStatusOptionsService;
